import { IndirectStorageBufferAttribute, StorageBufferAttribute } from 'three/webgpu';
import { assertStorageBufferBudget } from './bindingBudget';
import {
  createGpuOnlyStorageAttribute,
  type GpuOnlyStorageAllocation,
} from './gpuOnlyStorage';
import { localId, storage, workgroupId, wgslFn } from './tslCompat';

const WORKGROUP_SIZE = 256;

/**
 * Production watchdog for correctness-critical GPU readbacks.  These maps are
 * intentionally asynchronous, but an unresolved browser/device promise must
 * not leave logical physics frozen forever.
 */
export const DEFAULT_CONSTRAINT_SAFETY_READBACK_TIMEOUT_MS = 10_000;

export function normalizeConstraintSafetyReadbackTimeoutMs(value?: number): number {
  if (!Number.isFinite(value)) return DEFAULT_CONSTRAINT_SAFETY_READBACK_TIMEOUT_MS;
  return Math.max(1, Math.min(120_000, Math.floor(value!)));
}

/** Broadphase candidate/refit/traversal graph was incomplete. */
export const CONSTRAINT_SAFETY_BROADPHASE_FATAL_BIT = 1 << 0;
/** Contact pool, active list, or body-adjacency graph was incomplete. */
export const CONSTRAINT_SAFETY_CONTACT_FATAL_BIT = 1 << 1;
/** CCD could not produce a finite conservative result. */
export const CONSTRAINT_SAFETY_CCD_FATAL_BIT = 1 << 2;

export const CONSTRAINT_SAFETY_ALL_FATAL_BITS =
  CONSTRAINT_SAFETY_BROADPHASE_FATAL_BIT
  | CONSTRAINT_SAFETY_CONTACT_FATAL_BIT
  | CONSTRAINT_SAFETY_CCD_FATAL_BIT;

/**
 * GPU-side transaction around the committed rigid-body state.
 *
 * ContactGeneration writes `fatalBodyDispatch` only after every contact/CCD/
 * compact validator has run.  On a healthy substep both kernels below are
 * zero-dispatch indirect calls.  On a fatal substep the first kernel snapshots
 * the only committed component not already preserved by Integration
 * (`angularVelocities`) and erases the incomplete body adjacency before any
 * AVBD/sleeping kernel can consume it.  The final kernel restores position,
 * orientation and velocities in queue order, before rendering can observe the
 * partially solved state.
 */
export class ConstraintSafetyTransactionStage {
  private readonly renderer: any;
  private readonly device: GPUDevice;
  private readonly maxBodies: number;
  private readonly angularSnapshotAllocation: GpuOnlyStorageAllocation<Float32Array>;
  private readonly constraintCountSnapshotAllocation: GpuOnlyStorageAllocation<Uint32Array>;
  private readonly fatalBodyDispatchAttr: IndirectStorageBufferAttribute;
  private readonly captureAndGateKernel: any;
  private readonly rollbackKernel: any;
  private sleepingStateSnapshotAllocation?: GpuOnlyStorageAllocation<Uint32Array>;
  private sleepingPoseSnapshotAllocation?: GpuOnlyStorageAllocation<Float32Array>;
  private sleepingCaptureKernel?: any;
  private sleepingRollbackKernel?: any;

  constructor(options: {
    renderer: any;
    device: GPUDevice;
    safetyGate: StorageBufferAttribute;
    positions: StorageBufferAttribute;
    initialPose: StorageBufferAttribute;
    quaternions: StorageBufferAttribute;
    velocities: StorageBufferAttribute;
    prevLinearVelocities: StorageBufferAttribute;
    angularVelocities: StorageBufferAttribute;
    pairBodyContactCounts: StorageBufferAttribute;
    bodyConstraintCounts: StorageBufferAttribute;
    maxBodies: number;
  }) {
    const {
      renderer,
      device,
      safetyGate,
      positions,
      initialPose,
      quaternions,
      velocities,
      prevLinearVelocities,
      angularVelocities,
      pairBodyContactCounts,
      bodyConstraintCounts,
      maxBodies,
    } = options;

    this.renderer = renderer;
    this.device = device;
    this.maxBodies = maxBodies;

    const initialized = (() => {
      let angularSnapshotAllocation: GpuOnlyStorageAllocation<Float32Array> | undefined;
      let constraintCountSnapshotAllocation: GpuOnlyStorageAllocation<Uint32Array> | undefined;
      try {
        angularSnapshotAllocation = createGpuOnlyStorageAttribute(renderer, device, {
          scalarType: 'float32',
          count: maxBodies,
          itemSize: 4,
          label: 'AVBD fatal-substep angular velocity snapshot',
        });
        constraintCountSnapshotAllocation = createGpuOnlyStorageAttribute(renderer, device, {
          scalarType: 'uint32',
          count: maxBodies,
          itemSize: 1,
          label: 'AVBD fatal-substep constraint-count snapshot',
        });
        const fatalBodyDispatchAttr = new IndirectStorageBufferAttribute(
          new Uint32Array([0, 1, 1]),
          1,
        );
        fatalBodyDispatchAttr.name = 'AVBD Fatal Body Rollback Dispatch Indirect';

        const captureAndGateShader = wgslFn(/* wgsl */`
      fn compute(
        safetyGate: ptr<storage, array<atomic<u32>>, read_write>,
        angularVelocities: ptr<storage, array<vec4f>, read>,
        angularSnapshot: ptr<storage, array<vec4f>, read_write>,
        pairBodyContactCounts: ptr<storage, array<u32>, read_write>,
        bodyConstraintCounts: ptr<storage, array<u32>, read_write>,
        constraintCountSnapshot: ptr<storage, array<u32>, read_write>,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        // The indirect dispatch is zero in the healthy case.  Keep the gate
        // check as defense in depth if a backend ever reuses stale arguments.
        if (gid >= ${maxBodies}u || atomicLoad(&safetyGate[0u]) == 0u) { return; }
        angularSnapshot[gid] = angularVelocities[gid];
        constraintCountSnapshot[gid] = bodyConstraintCounts[gid];
        // An incomplete adjacency graph must not be consumed even transiently
        // by sleeping/coloring/body-solve kernels later in this command buffer.
        pairBodyContactCounts[gid] = 0u;
        bodyConstraintCounts[gid] = 0u;
      }
    `);
        const captureAndGateKernel = captureAndGateShader({
          safetyGate: storage(safetyGate, 'uint', 2).toAtomic(),
          angularVelocities: storage(angularVelocities, 'vec4f', maxBodies).toReadOnly(),
          angularSnapshot: storage(
            angularSnapshotAllocation.attribute,
            'vec4f',
            maxBodies,
          ),
          pairBodyContactCounts: storage(pairBodyContactCounts, 'uint', maxBodies),
          bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', maxBodies),
          constraintCountSnapshot: storage(
            constraintCountSnapshotAllocation.attribute,
            'uint',
            maxBodies,
          ),
          workgroupId,
          localId,
        }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName(
          'AVBD Fatal Substep Snapshot + Constraint Gate',
        );
        assertStorageBufferBudget('AVBD Fatal Substep Snapshot + Constraint Gate', 6);

        const rollbackShader = wgslFn(/* wgsl */`
      fn compute(
        safetyGate: ptr<storage, array<atomic<u32>>, read_write>,
        initialPose: ptr<storage, array<vec4f>, read>,
        positions: ptr<storage, array<vec4f>, read_write>,
        quaternions: ptr<storage, array<vec4f>, read_write>,
        velocities: ptr<storage, array<vec4f>, read_write>,
        prevLinearVelocities: ptr<storage, array<vec4f>, read>,
        angularVelocities: ptr<storage, array<vec4f>, read_write>,
        angularSnapshot: ptr<storage, array<vec4f>, read>,
        bodyConstraintCounts: ptr<storage, array<u32>, read_write>,
        constraintCountSnapshot: ptr<storage, array<u32>, read>,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= ${maxBodies}u || atomicLoad(&safetyGate[0u]) == 0u) { return; }
        let poseBase = gid * 2u;
        positions[gid] = initialPose[poseBase];
        quaternions[gid] = initialPose[poseBase + 1u];
        // AVBD Finalize Velocities stores the exact pre-finalize linear value
        // in prevLinearVelocities before writing its solved velocity.
        velocities[gid] = prevLinearVelocities[gid];
        angularVelocities[gid] = angularSnapshot[gid];
        bodyConstraintCounts[gid] = constraintCountSnapshot[gid];
      }
    `);
        const rollbackKernel = rollbackShader({
          safetyGate: storage(safetyGate, 'uint', 2).toAtomic(),
          initialPose: storage(initialPose, 'vec4f', maxBodies * 2).toReadOnly(),
          positions: storage(positions, 'vec4f', maxBodies),
          quaternions: storage(quaternions, 'vec4f', maxBodies),
          velocities: storage(velocities, 'vec4f', maxBodies),
          prevLinearVelocities: storage(prevLinearVelocities, 'vec4f', maxBodies).toReadOnly(),
          angularVelocities: storage(angularVelocities, 'vec4f', maxBodies),
          angularSnapshot: storage(
            angularSnapshotAllocation.attribute,
            'vec4f',
            maxBodies,
          ).toReadOnly(),
          bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', maxBodies),
          constraintCountSnapshot: storage(
            constraintCountSnapshotAllocation.attribute,
            'uint',
            maxBodies,
          ).toReadOnly(),
          workgroupId,
          localId,
        }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Fatal Substep Rollback');
        assertStorageBufferBudget('AVBD Fatal Substep Rollback', 10);

        return {
          angularSnapshotAllocation,
          constraintCountSnapshotAllocation,
          fatalBodyDispatchAttr,
          captureAndGateKernel,
          rollbackKernel,
        };
      } catch (error) {
        // The stage is not assigned to PhysicsEngine until this constructor
        // returns, so its outer init catch cannot dispose a partially built
        // instance. Release raw allocations here in reverse creation order.
        constraintCountSnapshotAllocation?.destroy();
        angularSnapshotAllocation?.destroy();
        throw error;
      }
    })();

    this.angularSnapshotAllocation = initialized.angularSnapshotAllocation;
    this.constraintCountSnapshotAllocation = initialized.constraintCountSnapshotAllocation;
    this.fatalBodyDispatchAttr = initialized.fatalBodyDispatchAttr;
    this.captureAndGateKernel = initialized.captureAndGateKernel;
    this.rollbackKernel = initialized.rollbackKernel;
  }

  /**
   * Adds exact rollback for the persistent sleeping state. This is attached
   * only when the optional sleeping stage exists, so scenes with joints,
   * springs, or sleeping disabled allocate none of these buffers.
   */
  attachSleepingState(options: {
    safetyGate: StorageBufferAttribute;
    bodyState: StorageBufferAttribute;
    stableFrames: StorageBufferAttribute;
    poseHistory: StorageBufferAttribute;
  }): void {
    if (this.sleepingCaptureKernel || this.sleepingRollbackKernel) {
      throw new Error('Constraint safety sleeping state was already attached.');
    }
    const { safetyGate, bodyState, stableFrames, poseHistory } = options;
    const maxBodies = this.maxBodies;
    this.sleepingStateSnapshotAllocation = createGpuOnlyStorageAttribute(
      this.renderer,
      this.device,
      {
        scalarType: 'uint32',
        count: maxBodies * 2,
        itemSize: 1,
        label: 'AVBD fatal-substep sleeping state snapshot',
      },
    );
    this.sleepingPoseSnapshotAllocation = createGpuOnlyStorageAttribute(
      this.renderer,
      this.device,
      {
        scalarType: 'float32',
        count: maxBodies * 2,
        itemSize: 4,
        label: 'AVBD fatal-substep sleeping pose-history snapshot',
      },
    );

    const captureShader = wgslFn(/* wgsl */`
      fn compute(
        safetyGate: ptr<storage, array<atomic<u32>>, read_write>,
        bodyState: ptr<storage, array<u32>, read>,
        stableFrames: ptr<storage, array<u32>, read>,
        poseHistory: ptr<storage, array<vec4f>, read>,
        stateSnapshot: ptr<storage, array<u32>, read_write>,
        poseSnapshot: ptr<storage, array<vec4f>, read_write>,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= ${maxBodies}u || atomicLoad(&safetyGate[0u]) == 0u) { return; }
        let stateBase = gid * 2u;
        stateSnapshot[stateBase] = bodyState[gid];
        stateSnapshot[stateBase + 1u] = stableFrames[gid];
        poseSnapshot[stateBase] = poseHistory[stateBase];
        poseSnapshot[stateBase + 1u] = poseHistory[stateBase + 1u];
      }
    `);
    this.sleepingCaptureKernel = captureShader({
      safetyGate: storage(safetyGate, 'uint', 2).toAtomic(),
      bodyState: storage(bodyState, 'uint', maxBodies).toReadOnly(),
      stableFrames: storage(stableFrames, 'uint', maxBodies).toReadOnly(),
      poseHistory: storage(poseHistory, 'vec4f', maxBodies * 2).toReadOnly(),
      stateSnapshot: storage(
        this.sleepingStateSnapshotAllocation.attribute,
        'uint',
        maxBodies * 2,
      ),
      poseSnapshot: storage(
        this.sleepingPoseSnapshotAllocation.attribute,
        'vec4f',
        maxBodies * 2,
      ),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName(
      'AVBD Fatal Substep Sleeping State Snapshot',
    );
    assertStorageBufferBudget('AVBD Fatal Substep Sleeping State Snapshot', 6);

    const rollbackShader = wgslFn(/* wgsl */`
      fn compute(
        safetyGate: ptr<storage, array<atomic<u32>>, read_write>,
        bodyState: ptr<storage, array<u32>, read_write>,
        stableFrames: ptr<storage, array<u32>, read_write>,
        poseHistory: ptr<storage, array<vec4f>, read_write>,
        stateSnapshot: ptr<storage, array<u32>, read>,
        poseSnapshot: ptr<storage, array<vec4f>, read>,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= ${maxBodies}u || atomicLoad(&safetyGate[0u]) == 0u) { return; }
        let stateBase = gid * 2u;
        bodyState[gid] = stateSnapshot[stateBase];
        stableFrames[gid] = stateSnapshot[stateBase + 1u];
        poseHistory[stateBase] = poseSnapshot[stateBase];
        poseHistory[stateBase + 1u] = poseSnapshot[stateBase + 1u];
      }
    `);
    this.sleepingRollbackKernel = rollbackShader({
      safetyGate: storage(safetyGate, 'uint', 2).toAtomic(),
      bodyState: storage(bodyState, 'uint', maxBodies),
      stableFrames: storage(stableFrames, 'uint', maxBodies),
      poseHistory: storage(poseHistory, 'vec4f', maxBodies * 2),
      stateSnapshot: storage(
        this.sleepingStateSnapshotAllocation.attribute,
        'uint',
        maxBodies * 2,
      ).toReadOnly(),
      poseSnapshot: storage(
        this.sleepingPoseSnapshotAllocation.attribute,
        'vec4f',
        maxBodies * 2,
      ).toReadOnly(),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName(
      'AVBD Fatal Substep Sleeping State Rollback',
    );
    assertStorageBufferBudget('AVBD Fatal Substep Sleeping State Rollback', 6);
  }

  getFatalBodyDispatchAttribute(): IndirectStorageBufferAttribute {
    return this.fatalBodyDispatchAttr;
  }

  captureAndGate(renderer: any): void {
    renderer.compute(this.captureAndGateKernel, this.fatalBodyDispatchAttr);
    if (this.sleepingCaptureKernel) {
      renderer.compute(this.sleepingCaptureKernel, this.fatalBodyDispatchAttr);
    }
  }

  rollback(renderer: any): void {
    if (this.sleepingRollbackKernel) {
      renderer.compute(this.sleepingRollbackKernel, this.fatalBodyDispatchAttr);
    }
    renderer.compute(this.rollbackKernel, this.fatalBodyDispatchAttr);
  }

  reset(): void {
    const dispatch = this.fatalBodyDispatchAttr.array as Uint32Array;
    dispatch[0] = 0;
    dispatch[1] = 1;
    dispatch[2] = 1;
    this.fatalBodyDispatchAttr.needsUpdate = true;
  }

  dispose(): void {
    this.angularSnapshotAllocation.destroy();
    this.constraintCountSnapshotAllocation.destroy();
    this.sleepingStateSnapshotAllocation?.destroy();
    this.sleepingPoseSnapshotAllocation?.destroy();
  }
}
